import { EllipsisIcon, PencilIcon, Trash2Icon } from "lucide-react";
import { useCallback, useRef, useState, type DragEvent, type ReactElement } from "react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import type { VaultFolderDefinition } from "./vaultFolders";

function VaultDocsFolderIcon(): ReactElement {
  return (
    <svg
      viewBox="0 0 88 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="size-full"
    >
      <path
        d="M8 18C8 13.5817 11.5817 10 16 10H34.3431C36.404 10 38.3739 10.8189 39.8284 12.2426L44.1716 16.5858C45.6261 18.0404 47.596 18.8593 49.6569 18.8593H72C76.4183 18.8593 80 22.441 80 26.8593V58C80 62.4183 76.4183 66 72 66H16C11.5817 66 8 62.4183 8 58V18Z"
        fill="#5EB3FF"
      />
      <path
        d="M8 22C8 17.5817 11.5817 14 16 14H30.5C32.9853 14 35.3853 14.8954 37.1924 16.4746L41.8076 20.5254C43.6147 22.1046 46.0147 23 48.5 23H72C76.4183 23 80 26.5817 80 31V58C80 62.4183 76.4183 66 72 66H16C11.5817 66 8 62.4183 8 58V22Z"
        fill="#3D9AE8"
      />
    </svg>
  );
}

/** The dataTransfer type a dragged vault FILE carries, so a folder card can tell it apart from
 * an operating-system file drop landing on the same element. */
export const VAULT_ITEM_DRAG_TYPE = "application/x-morrow-vault-item";

export function VaultFolderCard({
  folder,
  label,
  count,
  selected,
  onSelect,
  onDropFiles,
  onDropItem,
  onRename,
  onDelete,
}: {
  folder: VaultFolderDefinition;
  label: string;
  count: number;
  previewItems?: readonly unknown[];
  selected: boolean;
  onSelect: () => void;
  /** Files dropped from the OS straight onto this folder — upload them INTO it. */
  onDropFiles?: (files: FileList) => void;
  /** A vault file dragged from the list onto this folder — move it here (or unfile on "All"). */
  onDropItem?: (itemId: string) => void;
  /** Rename this folder. Only passed for custom folders — the system buckets aren't editable. */
  onRename?: () => void;
  /** Delete this folder. Only passed for custom folders. */
  onDelete?: () => void;
}) {
  const { t } = useI18n();
  const [dropActive, setDropActive] = useState(false);
  const hasMenu = onRename !== undefined || onDelete !== undefined;
  // A card is small and contains children, so dragleave fires when the pointer crosses onto a
  // child. Checking whether the new target is still inside the card avoids the flicker a naive
  // handler gets; it is the pattern the agent window's drop zone already uses.
  const cardRef = useRef<HTMLDivElement | null>(null);

  const accepts = useCallback(
    (event: DragEvent<HTMLElement>): boolean => {
      const types = Array.from(event.dataTransfer.types);
      if (types.includes(VAULT_ITEM_DRAG_TYPE)) return onDropItem !== undefined;
      return types.includes("Files") && onDropFiles !== undefined;
    },
    [onDropFiles, onDropItem],
  );

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!accepts(event)) return;
      // Both preventDefault AND stopPropagation: the page has its own full-bleed drop handler,
      // and without stopping here both would fire and its overlay would stick.
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = Array.from(event.dataTransfer.types).includes(
        VAULT_ITEM_DRAG_TYPE,
      )
        ? "move"
        : "copy";
      setDropActive(true);
    },
    [accepts],
  );

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && cardRef.current?.contains(next)) return;
    setDropActive(false);
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!accepts(event)) return;
      event.preventDefault();
      event.stopPropagation();
      setDropActive(false);
      const itemId = event.dataTransfer.getData(VAULT_ITEM_DRAG_TYPE).trim();
      if (itemId) {
        onDropItem?.(itemId);
        return;
      }
      if (event.dataTransfer.files.length > 0) onDropFiles?.(event.dataTransfer.files);
    },
    [accepts, onDropFiles, onDropItem],
  );

  return (
    <div
      ref={cardRef}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      {...(dropActive ? { "data-drop-active": "true" } : {})}
      title={dropActive ? t("vault.dropIntoFolder", { name: label }) : undefined}
      className={cn(
        "group relative flex w-28 shrink-0 flex-col items-center rounded-2xl border border-transparent transition-[transform,box-shadow,background-color,border-color] duration-200",
        selected
          ? "border-primary/30 bg-primary/8 shadow-sm"
          : "hover:border-border/60 hover:bg-muted/30 hover:shadow-md",
        !selected && !dropActive && "hover:-translate-y-0.5",
        // Unmistakable while a drag hovers: this is the one moment the target must be obvious.
        dropActive && "scale-[1.03] border-primary border-dashed bg-primary/10 shadow-lg",
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="flex w-full flex-col items-center rounded-2xl px-2 py-3 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        <span className="mb-2.5 flex w-full items-center justify-center" aria-hidden="true">
          <span className="block aspect-[88/72] w-[72%] max-w-[5.5rem]">
            <VaultDocsFolderIcon />
          </span>
        </span>
        <span className="line-clamp-2 text-sm font-semibold text-foreground">{label}</span>
        <span className="mt-0.5 text-xs text-muted-foreground">
          {t("vault.filesCount", { count })}
        </span>
        <span className="sr-only">{folder.id}</span>
      </button>

      {hasMenu ? (
        <Menu>
          <MenuTrigger
            render={
              <button
                type="button"
                aria-label={t("vault.folderActions")}
                title={t("vault.folderActions")}
                // Always visible on touch (no hover); fades in on hover for pointer devices.
                className="absolute right-1 top-1 flex size-7 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-colors hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 data-[popup-open]:opacity-100"
              >
                <EllipsisIcon className="size-4" aria-hidden="true" />
              </button>
            }
          />
          <MenuPopup align="end" className="min-w-40">
            {onRename ? (
              <MenuItem onClick={onRename}>
                <PencilIcon className="size-4" aria-hidden="true" />
                {t("vault.renameFolder")}
              </MenuItem>
            ) : null}
            {onDelete ? (
              <MenuItem variant="destructive" onClick={onDelete}>
                <Trash2Icon className="size-4" aria-hidden="true" />
                {t("vault.deleteFolder")}
              </MenuItem>
            ) : null}
          </MenuPopup>
        </Menu>
      ) : null}
    </div>
  );
}
